import { useCallback, useRef, useState, type ReactNode } from "react";
import { Head, Link, router, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCorners,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type KeyboardCoordinateGetter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AppLayout from "@/layouts/app_layout";
import { PageCanvas, PageHeader } from "@/components/page-canvas";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { FormErrors } from "@/components/forms/form-errors";
import {
  locationJobBoardPath,
  locationJobPath,
  locationJobsPath,
  newLocationJobPath,
  locationJobBoardPositionPath,
} from "@/routes";
import { dateTime, money, type Stage } from "./types";
import {
  boardMove,
  boardDrop,
  boardEdgeMove,
  stages,
  type BoardColumn,
  type BoardJob,
  type BoardMove,
} from "@/lib/job-board";

function BoardCard({
  job,
  stage,
  busy,
  onMove,
}: {
  job: BoardJob;
  stage: Stage;
  busy: boolean;
  onMove: (job: BoardJob) => void;
}) {
  const { auth, i18n } = usePage().props;
  const { t } = useTranslation();
  const name = `${job.representation.vehicle.make} ${job.representation.vehicle.model}${job.representation.vehicle.registration_number ? ` ${job.representation.vehicle.registration_number}` : ""} · ${job.representation.customer.name}`;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: job.id,
    data: { stage, kind: "job" },
    disabled: busy || !job.can_reorder,
    attributes: {
      role: "group",
      roleDescription: t("board_interactions.card"),
    },
  });
  const setCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      setActivatorNodeRef(node);
    },
    [setNodeRef, setActivatorNodeRef],
  );
  return (
    <Card
      ref={setCardRef}
      {...(job.can_reorder ? attributes : {})}
      {...listeners}
      data-job-id={job.id}
      data-dragging={isDragging || undefined}
      aria-label={
        job.can_reorder ? t("board_interactions.drag", { name }) : undefined
      }
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`${job.can_reorder ? "cursor-grab select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing" : ""} ${isDragging ? "opacity-40" : ""}`}
      onMouseDown={(event) => {
        if (
          event.button === 0 &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.altKey &&
          !event.shiftKey
        )
          listeners?.onMouseDown?.(event);
      }}
      onKeyDown={(event) => {
        if (
          event.target === event.currentTarget &&
          ((event.key === "F10" && event.shiftKey) ||
            event.key === "ContextMenu") &&
          job.can_reorder &&
          !busy
        ) {
          event.preventDefault();
          onMove(job);
        } else listeners?.onKeyDown?.(event);
      }}
      onClick={() => {
        if (job.can_reorder && !busy && !isDragging) onMove(job);
      }}
      onContextMenu={(event) => {
        if (!job.can_reorder || busy) return;
        event.preventDefault();
        if (!isDragging) onMove(job);
      }}
    >
      <CardContent className="space-y-3 pt-4 text-sm">
        <div className="flex items-start gap-2">
          <Link
            className="min-w-0 flex-1 font-medium underline break-words underline-offset-4"
            href={locationJobPath(auth.location!.id, job.id)}
            draggable={false}
            onClick={(event) => event.stopPropagation()}
            onContextMenu={(event) => event.stopPropagation()}
          >
            {job.representation.vehicle.make} {job.representation.vehicle.model}{" "}
            · {job.representation.customer.name}
          </Link>
        </div>
        {job.representation.vehicle.registration_number && (
          <p className="text-muted-foreground">
            {job.representation.vehicle.registration_number}
          </p>
        )}
        <p className="text-muted-foreground break-words">
          {job.bay_name} ·{" "}
          {t(`job_board.capacity_states.${job.capacity.state}`)}
        </p>
        <p>{dateTime(job.starts_at, job.time_zone, i18n.locale)}</p>
        <p>
          {t("job_board.balance", {
            amount: money(
              job.outstanding_minor,
              job.representation.total.currency,
              i18n.locale,
            ),
          })}
        </p>
        {job.advance_overdue && <p role="alert">{t("payments.overdue")}</p>}
        <p>
          {job.collected_at
            ? t("job_board.collected")
            : t("jobs.not_collected")}
        </p>
      </CardContent>
    </Card>
  );
}
function StageColumn({
  column,
  children,
  disabled,
}: {
  column: BoardColumn;
  children: ReactNode;
  disabled: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `stage:${column.stage}`,
    data: { stage: column.stage, kind: "stage" },
    disabled,
  });
  return (
    <section
      ref={setNodeRef}
      data-stage={column.stage}
      aria-labelledby={`stage-${column.stage}`}
      className={`min-w-0 space-y-3 rounded-xl bg-muted p-3 ${isOver ? "ring-2 ring-primary" : ""}`}
    >
      {children}
    </section>
  );
}

export default function JobBoard({
  columns,
  date,
  through_date,
  time_zone,
  overdue_advance_jobs,
  board_version,
  billing_staff,
}: {
  columns: BoardColumn[];
  date: string;
  through_date: string;
  time_zone: string;
  overdue_advance_jobs: number;
  board_version: string;
  billing_staff: { id: string; name: string }[];
}) {
  const { auth, i18n, errors } = usePage().props;
  const { t } = useTranslation();
  const [active, setActive] = useState<string | null>(null),
    [busy, setBusy] = useState(false),
    [pending, setPending] = useState<BoardMove | null>(null);
  const [menuJob, setMenuJob] = useState<BoardJob | null>(null),
    [menuStage, setMenuStage] = useState<Stage>("to_do"),
    [anchor, setAnchor] = useState(""),
    [side, setSide] = useState<"before" | "after" | "start" | "end">("before");
  const [arrival, setArrival] = useState(""),
    [billing, setBilling] = useState(""),
    [announcement, setAnnouncement] = useState("");
  const sourceHandle = useRef<HTMLElement | null>(null);
  const sourceJob = useRef<string | null>(null);
  const activeJob = columns
    .flatMap((column) => column.jobs)
    .find((job) => job.id === active);
  const allowed = (stage: Stage) =>
    !activeJob ||
    activeJob.work_state === stage ||
    activeJob.move_to.includes(stage);
  const coordinates: KeyboardCoordinateGetter = (
    event,
    { active: activeId, context },
  ) => {
    if (
      !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.code)
    )
      return;
    event.preventDefault();
    const currentId = String(context.over?.id || activeId);
    const currentColumn = columns.find(
      (column) =>
        `stage:${column.stage}` === currentId ||
        column.jobs.some((job) => job.id === currentId),
    );
    if (!currentColumn) return;
    let target: string | undefined;
    if (event.code === "ArrowLeft" || event.code === "ArrowRight") {
      const direction = event.code === "ArrowLeft" ? -1 : 1;
      for (
        let index = stages.indexOf(currentColumn.stage) + direction;
        index >= 0 && index < stages.length;
        index += direction
      ) {
        const next = columns.find((column) => column.stage === stages[index]);
        if (next && allowed(next.stage)) {
          target =
            next.jobs.find((job) => job.id !== String(activeId))?.id ||
            `stage:${next.stage}`;
          break;
        }
      }
    } else {
      const ids = currentColumn.jobs.map((job) => job.id),
        index = ids.indexOf(currentId);
      target = ids[index + (event.code === "ArrowDown" ? 1 : -1)];
    }
    const rect = target ? context.droppableRects.get(target) : undefined;
    if (rect) return { x: rect.left, y: rect.top };
  };
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: coordinates }),
  );
  const collision: CollisionDetection = (args) => {
    const filtered = {
      ...args,
      droppableContainers: args.droppableContainers.filter((container) =>
        allowed(container.data.current?.stage),
      ),
    };
    const pointer = pointerWithin(filtered);
    return pointer.length
      ? [
          ...pointer.filter((item) => !String(item.id).startsWith("stage:")),
          ...pointer.filter((item) => String(item.id).startsWith("stage:")),
        ]
      : closestCorners(filtered);
  };
  if (!auth.location) return null;
  const location = auth.location.id;
  const pages = Object.fromEntries(
    columns.map((column) => [`${column.stage}_page`, column.pagination.page]),
  );
  const restore = () =>
    requestAnimationFrame(() => {
      const handle = sourceJob.current
        ? document.querySelector<HTMLElement>(
            `[data-job-id="${sourceJob.current}"][tabindex]`,
          )
        : null;
      (handle || sourceHandle.current)?.focus();
    });
  const save = (
    intent: BoardMove,
    choices: { send_arrival?: string; billing_assignee_id?: string } = {},
  ) => {
    const job = columns
      .flatMap((column) => column.jobs)
      .find((item) => item.id === intent.jobId);
    if (!job || busy) return;
    setBusy(true);
    router.patch(
      locationJobBoardPositionPath(location, job.id),
      {
        ...pages,
        board_position: {
          revision: job.revision,
          board_version,
          date,
          stage: intent.stage,
          before_id: intent.before_id,
          after_id: intent.after_id,
          ...choices,
        },
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setPending(null);
          setMenuJob(null);
          setAnnouncement(t("board_interactions.saved"));
          restore();
        },
        onError: () => {
          setPending(null);
          setMenuJob(null);
          setAnnouncement(t("board_interactions.restored"));
          restore();
        },
        onFinish: () => setBusy(false),
      },
    );
  };
  const requestMove = (intent: BoardMove | null) => {
    if (!intent) return;
    const job = columns
      .flatMap((column) => column.jobs)
      .find((item) => item.id === intent.jobId)!;
    if (
      intent.stage !== job.work_state &&
      ["in_progress", "billing"].includes(intent.stage)
    ) {
      setArrival("");
      setBilling("");
      setMenuJob(null);
      setPending(intent);
    } else save(intent);
  };
  const day = (value: string) =>
    new Intl.DateTimeFormat(i18n.locale, {
      dateStyle: "medium",
      timeZone: "UTC",
    }).format(new Date(`${value}T12:00:00Z`));
  const name = (id: string | number) =>
    columns
      .flatMap((column) => column.jobs)
      .find((job) => job.id === String(id))?.representation.customer.name || "";
  const targetName = (id: string | number) => {
    const stage = columns.find(
      (column) =>
        `stage:${column.stage}` === String(id) ||
        column.jobs.some((job) => job.id === String(id)),
    )?.stage;
    return stage ? t(`job_board.stages.${stage}`) : "";
  };
  const menuRows =
    columns
      .find((column) => column.stage === menuStage)
      ?.jobs.filter((job) => job.id !== menuJob?.id) || [];
  return (
    <AppLayout
      breadcrumbs={[
        { title: t("job_board.title"), href: locationJobBoardPath(location) },
      ]}
    >
      <Head title={t("job_board.title")} />
      <PageCanvas>
        <PageHeader
          title={t("job_board.title")}
          description={
            <>
              {t("job_board.range", {
                from: day(date),
                through: day(through_date),
                zone: time_zone,
              })}
              <br />
              {t("job_board.done_help")}
            </>
          }
          actions={
            <Button
              render={
                <Link
                  href={newLocationJobPath(location, {
                    composition: {
                      origin: "board",
                      ...Object.fromEntries(
                        Object.entries(pages).filter(([, page]) => page > 1),
                      ),
                    },
                  })}
                />
              }
            >
              {t("jobs.create")}
            </Button>
          }
        />
        <FormErrors errors={errors} />
        <p className="text-muted-foreground text-sm">
          {t("board_interactions.help")}
        </p>
        <div className="sr-only" role="status" aria-live="polite">
          {announcement}
        </div>
        {overdue_advance_jobs > 0 && (
          <div role="alert" className="space-y-2 rounded-lg border p-4">
            <p>
              {t("payments.overdue_summary", { count: overdue_advance_jobs })}
            </p>
            <Link
              className="underline"
              href={locationJobsPath(location, { advance_overdue: "1" })}
            >
              {t("payments.review_overdue")}
            </Link>
          </div>
        )}
        <DndContext
          id={`job-board-${location}`}
          sensors={sensors}
          collisionDetection={collision}
          accessibility={{
            screenReaderInstructions: {
              draggable: t("board_interactions.instructions"),
            },
            announcements: {
              onDragStart: ({ active }) =>
                t("board_interactions.picked", { name: name(active.id) }),
              onDragOver: ({ active: dragged, over }) => {
                if (!over) return t("board_interactions.no_target");
                const column = columns.find(
                  (item) =>
                    `stage:${item.stage}` === String(over.id) ||
                    item.jobs.some((job) => job.id === String(over.id)),
                );
                return t("board_interactions.destination_position", {
                  stage: targetName(over.id),
                  position: column?.jobs.some(
                    (job) => job.id === String(over.id),
                  )
                    ? column.jobs.findIndex(
                        (job) => job.id === String(over.id),
                      ) + 1
                    : (column?.jobs.filter(
                        (job) => job.id !== String(dragged.id),
                      ).length || 0) + 1,
                });
              },
              onDragEnd: () => t("board_interactions.dropped"),
              onDragCancel: () => t("board_interactions.cancelled"),
            },
          }}
          onDragStart={({ active }) => {
            setActive(String(active.id));
            sourceJob.current = String(active.id);
            sourceHandle.current = document.activeElement as HTMLElement;
          }}
          onDragCancel={() => {
            setActive(null);
            restore();
          }}
          onDragEnd={({ active: dragged, over, delta, activatorEvent }) => {
            setActive(null);
            if (!over) return restore();
            const keyboard = activatorEvent.type === "keydown";
            const overRect = over.rect;
            const rect = dragged.rect.current.translated;
            const after = keyboard
              ? delta.y > 0
              : !!rect &&
                rect.top + rect.height / 2 > overRect.top + overRect.height / 2;
            requestMove(
              boardDrop(
                columns,
                String(dragged.id),
                String(over.id),
                after ? "after" : "before",
              ),
            );
          }}
        >
          <div
            role="region"
            aria-label={t("job_board.title")}
            aria-busy={busy}
            tabIndex={0}
            className="overflow-x-auto pb-3 focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="grid min-w-[1320px] grid-cols-5 items-start gap-4">
              {columns.map((column) => (
                <StageColumn
                  key={column.stage}
                  column={column}
                  disabled={busy || !allowed(column.stage)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <h2
                      id={`stage-${column.stage}`}
                      className="font-semibold break-words"
                    >
                      {t(`job_board.stages.${column.stage}`)}
                    </h2>
                    <Badge variant="secondary">{column.total}</Badge>
                  </div>
                  <SortableContext
                    id={column.stage}
                    items={column.jobs.map((job) => job.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {!column.jobs.length && (
                      <p className="text-muted-foreground min-h-24 py-4 text-sm">
                        {t("job_board.empty")}
                      </p>
                    )}
                    {column.jobs.map((job) => (
                      <BoardCard
                        key={job.id}
                        job={job}
                        stage={column.stage}
                        busy={busy}
                        onMove={(selected) => {
                          sourceHandle.current =
                            document.activeElement as HTMLElement;
                          sourceJob.current = selected.id;
                          setMenuJob(selected);
                          setMenuStage(column.stage);
                          const first = column.jobs.find(
                            (item) => item.id !== selected.id,
                          );
                          setAnchor(first?.id || "");
                          setSide("before");
                        }}
                      />
                    ))}
                  </SortableContext>
                  {column.pagination.pages > 1 && (
                    <nav
                      className="flex flex-wrap justify-between gap-3 text-sm"
                      aria-label={t("job_board.pages", {
                        stage: t(`job_board.stages.${column.stage}`),
                      })}
                    >
                      <p className="w-full">
                        {t("board_interactions.page", {
                          page: column.pagination.page,
                          pages: column.pagination.pages,
                        })}
                      </p>
                      {column.pagination.page > 1 && (
                        <Link
                          preserveScroll
                          className="min-h-11 py-2 underline"
                          href={locationJobBoardPath(location, {
                            ...pages,
                            [`${column.stage}_page`]:
                              column.pagination.page - 1,
                          })}
                        >
                          {t("customers.previous")}
                        </Link>
                      )}
                      {column.pagination.page < column.pagination.pages && (
                        <Link
                          preserveScroll
                          className="min-h-11 py-2 underline"
                          href={locationJobBoardPath(location, {
                            ...pages,
                            [`${column.stage}_page`]:
                              column.pagination.page + 1,
                          })}
                        >
                          {t("customers.next")}
                        </Link>
                      )}
                    </nav>
                  )}
                </StageColumn>
              ))}
            </div>
          </div>
          <DragOverlay dropAnimation={null}>
            {activeJob ? (
              <div className="max-w-64 rounded-lg border bg-card p-4 shadow-lg">
                {activeJob.representation.customer.name}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
        <Sheet
          open={!!menuJob}
          onOpenChange={(open) => {
            if (!open) {
              setMenuJob(null);
              restore();
            }
          }}
        >
          <SheetContent
            finalFocus={sourceHandle}
            className="w-full! overflow-y-auto sm:max-w-lg!"
          >
            <SheetHeader>
              <SheetTitle>{t("board_interactions.move")}</SheetTitle>
              <SheetDescription>
                {t("board_interactions.menu_help")}
              </SheetDescription>
            </SheetHeader>
            <form
              className="space-y-4 px-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (menuJob)
                  requestMove(
                    side === "start" || side === "end"
                      ? boardEdgeMove(columns, menuJob.id, menuStage, side)
                      : boardMove(
                          columns,
                          menuJob.id,
                          anchor || `stage:${menuStage}`,
                          side,
                        ),
                  );
              }}
            >
              <Label htmlFor="move-stage">
                {t("board_interactions.stage")}
              </Label>
              <NativeSelect
                id="move-stage"
                className="w-full"
                value={menuStage}
                onChange={(event) => {
                  const stage = event.target.value as Stage;
                  setMenuStage(stage);
                  setAnchor(
                    columns
                      .find((column) => column.stage === stage)
                      ?.jobs.find((job) => job.id !== menuJob?.id)?.id || "",
                  );
                }}
              >
                {menuJob &&
                  [menuJob.work_state as Stage, ...menuJob.move_to].map(
                    (stage) => (
                      <NativeSelectOption key={stage} value={stage}>
                        {t(`job_board.stages.${stage}`)}
                      </NativeSelectOption>
                    ),
                  )}
              </NativeSelect>
              {
                <>
                  <Label htmlFor="move-side">
                    {t("board_interactions.position")}
                  </Label>
                  <NativeSelect
                    id="move-side"
                    className="w-full"
                    value={side}
                    onChange={(event) =>
                      setSide(
                        event.target.value as
                          "before" | "after" | "start" | "end",
                      )
                    }
                  >
                    <NativeSelectOption value="before">
                      {t("board_interactions.before")}
                    </NativeSelectOption>
                    <NativeSelectOption value="after">
                      {t("board_interactions.after")}
                    </NativeSelectOption>
                    <NativeSelectOption value="start">
                      {t("board_interactions.start")}
                    </NativeSelectOption>
                    <NativeSelectOption value="end">
                      {t("board_interactions.end")}
                    </NativeSelectOption>
                  </NativeSelect>
                  {menuRows.length > 0 &&
                    (side === "before" || side === "after") && (
                      <>
                        <Label htmlFor="move-anchor">
                          {t("board_interactions.neighbor")}
                        </Label>
                        <NativeSelect
                          id="move-anchor"
                          className="w-full"
                          value={anchor}
                          onChange={(event) => setAnchor(event.target.value)}
                        >
                          {menuRows.map((job) => (
                            <NativeSelectOption key={job.id} value={job.id}>
                              {job.representation.customer.name} ·{" "}
                              {job.representation.vehicle.make}{" "}
                              {job.representation.vehicle.model}
                            </NativeSelectOption>
                          ))}
                        </NativeSelect>
                      </>
                    )}
                </>
              }
              <div className="flex flex-wrap gap-3">
                <Button
                  type="submit"
                  disabled={
                    busy ||
                    (side === "start" || side === "end"
                      ? !menuJob ||
                        !boardEdgeMove(columns, menuJob.id, menuStage, side)
                      : menuRows.length === 0 &&
                        menuStage === menuJob?.work_state)
                  }
                >
                  {t("common.continue")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setMenuJob(null);
                    restore();
                  }}
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </form>
          </SheetContent>
        </Sheet>
        <Sheet
          open={!!pending}
          onOpenChange={(open) => {
            if (!open) {
              setPending(null);
              restore();
            }
          }}
        >
          <SheetContent
            finalFocus={sourceHandle}
            className="w-full! sm:max-w-lg!"
          >
            <SheetHeader>
              <SheetTitle>
                {t("board_interactions.confirm", {
                  stage: pending ? t(`job_board.stages.${pending.stage}`) : "",
                })}
              </SheetTitle>
              <SheetDescription>
                {t("board_interactions.confirm_help")}
              </SheetDescription>
            </SheetHeader>
            <form
              className="space-y-5 px-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (pending)
                  save(
                    pending,
                    pending.stage === "in_progress"
                      ? { send_arrival: arrival }
                      : { billing_assignee_id: billing },
                  );
              }}
            >
              <Label htmlFor="drop-choice">
                {t(
                  pending?.stage === "in_progress"
                    ? "job_board.send_arrival"
                    : "job_board.billing_assignee",
                )}
              </Label>
              <NativeSelect
                id="drop-choice"
                required
                className="w-full"
                value={pending?.stage === "in_progress" ? arrival : billing}
                onChange={(event) =>
                  pending?.stage === "in_progress"
                    ? setArrival(event.target.value)
                    : setBilling(event.target.value)
                }
              >
                <NativeSelectOption value="">
                  {t("jobs.choose")}
                </NativeSelectOption>
                {pending?.stage === "in_progress" ? (
                  <>
                    <NativeSelectOption value="true">
                      {t("job_board.send_yes")}
                    </NativeSelectOption>
                    <NativeSelectOption value="false">
                      {t("job_board.send_no")}
                    </NativeSelectOption>
                  </>
                ) : (
                  billing_staff.map((person) => (
                    <NativeSelectOption key={person.id} value={person.id}>
                      {person.name}
                    </NativeSelectOption>
                  ))
                )}
              </NativeSelect>
              <div className="flex flex-wrap gap-3">
                <Button type="submit" disabled={busy}>
                  {t("board_interactions.save_move")}
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setPending(null);
                    restore();
                  }}
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </form>
          </SheetContent>
        </Sheet>
      </PageCanvas>
    </AppLayout>
  );
}
